import React, { useState } from 'react'
import { useCssHandles } from '../hooks/useCssHandles'
import { Coupon } from '../types/battery'
import Input from './Input'

const CSS_HANDLES = [
  'couponSystem',
  'couponInput',
  'couponButton',
  'couponApplied',
  'couponError',
  'couponSuccess',
  'couponList',
  'couponItem',
  'couponCode',
  'couponDiscount',
  'couponRemove',
  'couponInfo',
  'couponType',
  'couponValue',
  'couponValidUntil',
  'couponDescription'
] as const

interface CouponSystemProps {
  appliedCoupons: Coupon[]
  onApplyCoupon: (couponCode: string) => Promise<{ success: boolean; message: string }>
  onRemoveCoupon: (couponId: string) => void
  subtotal: number
  className?: string
  disabled?: boolean
}

const CouponSystem: React.FC<CouponSystemProps> = ({
  appliedCoupons,
  onApplyCoupon,
  onRemoveCoupon,
  subtotal,
  className = '',
  disabled = false
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  const [couponCode, setCouponCode] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const calculateCouponDiscount = (coupon: Coupon, orderValue: number): number => {
    if ('discountAmount' in coupon) {
      const discount = (coupon as Coupon & { discountAmount?: number }).discountAmount
      if (typeof discount === 'number') {
        return discount
      }
    }

    if (coupon.minOrderValue && orderValue < coupon.minOrderValue) return 0

    switch (coupon.type) {
      case 'percentage': {
        const percentageDiscount = (orderValue * coupon.value) / 100
        return coupon.maxDiscount ? Math.min(percentageDiscount, coupon.maxDiscount) : percentageDiscount
      }
      case 'fixed':
        return Math.min(coupon.value, orderValue)
      case 'free_shipping':
        return 0 // Será aplicado no frete
      default:
        return 0
    }
  }

  const getTotalDiscount = (): number => {
    return appliedCoupons.reduce((total, coupon) => {
      return total + calculateCouponDiscount(coupon, subtotal)
    }, 0)
  }

  const handleApplyCoupon = async () => {
    setError('')
    setSuccess('')

    const trimmedCode = couponCode.trim()

    if (!trimmedCode) {
      setError('Digite um código de cupom')
      return
    }

    try {
      setIsLoading(true)
      const result = await onApplyCoupon(trimmedCode.toUpperCase())

      if (result.success) {
        setSuccess(result.message)
        setCouponCode('')
      } else {
        setError(result.message)
      }
    } catch (applyError) {
      const message =
        applyError instanceof Error
          ? applyError.message
          : 'Não foi possível aplicar o cupom. Tente novamente.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveCoupon = (couponId: string) => {
    onRemoveCoupon(couponId)
    setSuccess('Cupom removido com sucesso!')
  }

  const getCouponDisplayValue = (coupon: Coupon): string => {
    switch (coupon.type) {
      case 'percentage':
        return `${coupon.value}%`
      case 'fixed':
        return formatPrice(coupon.value)
      case 'free_shipping':
        return 'Frete Grátis'
      default:
        return ''
    }
  }

  return (
    <div className={`${handles.couponSystem} ${className}`}>
      {/* Input para aplicar cupom */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-[#FFD700]" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z" clipRule="evenodd" />
            <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-900">
            Cupom de Desconto
          </h3>
        </div>
        
        {disabled && (
          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
            <p className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Os cupons não podem ser alterados durante o pagamento
            </p>
          </div>
        )}
        
        <div className="flex gap-2">
          <Input
            type="text"
            value={couponCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCouponCode(e.target.value.toUpperCase())}
            placeholder="Código do cupom"
            disabled={disabled}
            className={`${handles.couponInput} flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60`}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && !isLoading && !disabled) {
                e.preventDefault()
                void handleApplyCoupon()
              }
            }}
          />
          <button
            onClick={() => void handleApplyCoupon()}
            disabled={isLoading || disabled}
            className={`${handles.couponButton} bg-gradient-to-r from-[#FFD700] to-[#FFC107] text-[#1E3A8A] px-6 py-2 rounded-lg font-semibold hover:shadow-md transition-all duration-200 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed`}
            aria-label="Aplicar cupom"
          >
            {isLoading ? 'Aplicando...' : 'Aplicar'}
          </button>
        </div>

        {/* Mensagens de feedback */}
        {error && (
          <div className={`${handles.couponError} mt-2 p-2.5 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded flex items-start gap-2`}>
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        
        {success && (
          <div className={`${handles.couponSuccess} mt-2 p-2.5 bg-green-50 border-l-4 border-green-500 text-green-700 text-sm rounded flex items-start gap-2`}>
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>{success}</span>
          </div>
        )}
      </div>

      {/* Lista de cupons aplicados */}
      {appliedCoupons.length > 0 && (
        <div className={`${handles.couponList} mb-4`}>
          <div className="space-y-2">
            {appliedCoupons.map(coupon => (
              <div 
                key={coupon.id} 
                className={`${handles.couponItem} group relative bg-gradient-to-r from-[#FFF8DC] to-white border-2 border-dashed border-[#FFD700] rounded-lg p-3 hover:shadow-md transition-all duration-200`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`${handles.couponInfo} flex-1 min-w-0`}>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`${handles.couponCode} text-sm font-bold text-[#1E3A8A] tracking-wide`}>
                        {coupon.code}
                      </span>
                      <span className={`${handles.couponType} inline-flex items-center text-xs bg-[#FFD700] text-[#1E3A8A] px-2 py-0.5 rounded-full font-medium`}>
                        {coupon.type === 'percentage' ? '% Desconto' : 
                         coupon.type === 'fixed' ? 'R$ Fixo' : 'Frete Grátis'}
                      </span>
                    </div>
                    
                    <div className={`${handles.couponDescription} text-xs text-gray-700 mb-1.5 line-clamp-1`}>
                      {coupon.description}
                    </div>
                    
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-[#FFD700]" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        <span className={`${handles.couponValue} font-semibold text-[#1E3A8A]`}>
                          {getCouponDisplayValue(coupon)}
                        </span>
                      </span>
                      <span className={`${handles.couponValidUntil}`}>
                        Até {formatDate(coupon.validUntil)}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleRemoveCoupon(coupon.id)}
                    disabled={disabled}
                    className={`${handles.couponRemove} flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50 group-hover:opacity-100 opacity-70 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-400 disabled:hover:bg-transparent`}
                    aria-label={`Remover cupom ${coupon.code}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resumo de descontos */}
      {appliedCoupons.length > 0 && getTotalDiscount() > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-green-800">Você economizou:</span>
            </div>
            <span className="text-base font-bold text-green-600">
              -{formatPrice(getTotalDiscount())}
            </span>
          </div>
        </div>
      )}

    </div>
  )
}

export default CouponSystem
